import React, { useMemo, useState } from 'react';
import { Collapse, Empty, Segmented, Tag, Timeline, Typography } from 'antd';
import { HistoryOutlined, LockOutlined } from '@ant-design/icons';
import { CandidateSummaryGapResolutionEvent } from '../models/candidate-summary.model';
import { ASSESSMENT_SECTIONS } from '../utils/candidate-summary-labels';
import {
  GAP_HISTORY_ACTION_COLOR,
  GAP_HISTORY_ACTION_LABEL,
  countResolvedGapHistory,
  describeGapHistoryEvent,
  formatGapHistoryTimestamp,
  gapHistoryEntriesNewestFirst,
} from '../utils/candidate-summary-gap-history';

const { Text, Paragraph } = Typography;

type HistoryFilter = 'all' | 'resolved';

interface CandidateSummaryGapHistoryPanelProps {
  history?: CandidateSummaryGapResolutionEvent[];
}

export const CandidateSummaryGapHistoryPanel: React.FC<
  CandidateSummaryGapHistoryPanelProps
> = ({ history }) => {
  const [filter, setFilter] = useState<HistoryFilter>('all');
  const resolvedCount = countResolvedGapHistory(history);
  const entries = useMemo(() => {
    const sorted = gapHistoryEntriesNewestFirst(history);
    if (filter === 'resolved') {
      return sorted.filter((event) => event.action === 'RESOLVED' || event.action === 'EDITED');
    }
    return sorted;
  }, [filter, history]);

  if (!history?.length) {
    return null;
  }

  return (
    <section
      aria-labelledby="gap-history-heading"
      className="rounded-lg border border-dashed border-gray-300 bg-white p-3 sm:p-4 text-left"
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between mb-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <HistoryOutlined aria-hidden className="text-gray-500" />
            <h3
              id="gap-history-heading"
              className="text-base font-semibold text-gray-800 m-0 text-left"
            >
              {ASSESSMENT_SECTIONS.gapHistoryTitle}
            </h3>
            <Tag icon={<LockOutlined aria-hidden />} className="rounded-md m-0">
              {ASSESSMENT_SECTIONS.gapHistoryInternalOnly}
            </Tag>
          </div>
          <Paragraph className="!mb-0 !text-left text-sm text-gray-500 leading-relaxed">
            {ASSESSMENT_SECTIONS.gapHistoryIntro}
          </Paragraph>
        </div>
        <Segmented
          className="w-full sm:w-auto"
          value={filter}
          onChange={(value) => setFilter(value as HistoryFilter)}
          options={[
            { label: `All (${history.length})`, value: 'all' },
            { label: `Closures (${resolvedCount})`, value: 'resolved' },
          ]}
        />
      </div>

      <Collapse
        bordered={false}
        defaultActiveKey={resolvedCount > 0 ? ['gap-history'] : []}
        className="bg-transparent [&_.ant-collapse-header]:!px-0 [&_.ant-collapse-content-box]:!px-0"
        items={[
          {
            key: 'gap-history',
            label: (
              <span className="text-sm font-medium text-gray-700">
                {resolvedCount > 0
                  ? `Review ${resolvedCount} gap closure${resolvedCount === 1 ? '' : 's'}`
                  : 'Show review timeline'}
              </span>
            ),
            children:
              entries.length > 0 ? (
                <Timeline
                  className="mt-2 text-left [&_.ant-timeline-item-content]:!min-h-0"
                  items={entries.map((event) => ({
                    color: GAP_HISTORY_ACTION_COLOR[event.action],
                    children: (
                      <article className="pb-1 text-left">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <Tag color={GAP_HISTORY_ACTION_COLOR[event.action]} className="rounded-md m-0">
                            {GAP_HISTORY_ACTION_LABEL[event.action]}
                          </Tag>
                          <Text type="secondary" className="text-xs">
                            {formatGapHistoryTimestamp(event.occurredAt)}
                          </Text>
                          <Text type="secondary" className="text-xs break-all">
                            {event.editedBy}
                          </Text>
                        </div>
                        {event.gapDescription && (
                          <Text className="block text-sm text-gray-800 leading-relaxed break-words">
                            {event.gapDescription}
                          </Text>
                        )}
                        {event.previousDescription &&
                          event.previousDescription !== event.gapDescription && (
                            <Text
                              type="secondary"
                              className="block text-xs leading-relaxed break-words mt-1"
                            >
                              {ASSESSMENT_SECTIONS.gapHistoryPreviousWording}:{' '}
                              {event.previousDescription}
                            </Text>
                          )}
                        <Text
                          type="secondary"
                          className="block text-sm leading-relaxed break-words mt-1"
                        >
                          {describeGapHistoryEvent(event)}
                        </Text>
                        {event.previousResolutionNote &&
                          event.previousResolutionNote !== event.resolutionNote && (
                            <Text
                              type="secondary"
                              className="block text-xs leading-relaxed break-words mt-1"
                            >
                              {ASSESSMENT_SECTIONS.gapHistoryPreviousNote}:{' '}
                              {event.previousResolutionNote}
                            </Text>
                          )}
                      </article>
                    ),
                  }))}
                />
              ) : (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={ASSESSMENT_SECTIONS.gapHistoryEmptyFilter}
                  className="my-2"
                />
              ),
          },
        ]}
      />
    </section>
  );
};
